/**
 * @zendir/ui - Tooltip Component
 *
 * Tooltip following Astro UX Design System.
 * Uses a portal to render in document.body, ensuring correct positioning
 * even when the trigger is inside containers with CSS transforms or overflow.
 *
 * @example
 * ```tsx
 * <Tooltip content="Click to enable telemetry">
 *   <Button>Enable</Button>
 * </Tooltip>
 * ```
 */

import React, {
  memo,
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  useCallback,
} from "react";
import ReactDOM from "react-dom";
import { useTheme } from "../../theme";
import { classNames } from "../../utils";

export type TooltipPlacement = "top" | "bottom" | "left" | "right";

export interface TooltipProps {
  /** Tooltip content */
  content: React.ReactNode;
  /** Placement */
  placement?: TooltipPlacement;
  /** Delay before showing (ms) */
  delay?: number;
  /** Disabled state */
  disabled?: boolean;
  /** Children element to trigger tooltip */
  children: React.ReactElement;
  /** Custom className */
  className?: string;
}

export const Tooltip = memo(function Tooltip({
  content,
  placement = "top",
  delay = 200,
  disabled = false,
  children,
  className = "",
}: TooltipProps): React.ReactElement {
  const { tokens } = useTheme();
  const [visible, setVisible] = useState(false);
  const [positioned, setPositioned] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<number>();
  const hideTimeoutRef = useRef<number>();

  const showTooltip = useCallback(() => {
    if (disabled) return;
    // Cancel any pending hide so moving between trigger ↔ tooltip keeps it open
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = undefined;
    }
    timeoutRef.current = window.setTimeout(() => {
      setVisible(true);
      setPositioned(false);
    }, delay);
  }, [disabled, delay]);

  const hideTooltip = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    // Small delay so user can move pointer from trigger to tooltip (WCAG 1.4.13)
    hideTimeoutRef.current = window.setTimeout(() => {
      setVisible(false);
      setPositioned(false);
    }, 100);
  }, []);

  const hideImmediately = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
    setVisible(false);
    setPositioned(false);
  }, []);

  const calculatePosition = useCallback(() => {
    if (!triggerRef.current || !tooltipRef.current) return;

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();
    const gap = 8;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let top = 0;
    let left = 0;

    switch (placement) {
      case "top":
        top = triggerRect.top - tooltipRect.height - gap;
        left = triggerRect.left + (triggerRect.width - tooltipRect.width) / 2;
        break;
      case "bottom":
        top = triggerRect.bottom + gap;
        left = triggerRect.left + (triggerRect.width - tooltipRect.width) / 2;
        break;
      case "left":
        top = triggerRect.top + (triggerRect.height - tooltipRect.height) / 2;
        left = triggerRect.left - tooltipRect.width - gap;
        break;
      case "right":
        top = triggerRect.top + (triggerRect.height - tooltipRect.height) / 2;
        left = triggerRect.right + gap;
        break;
    }

    // Keep tooltip within viewport bounds
    if (left < gap) left = gap;
    if (left + tooltipRect.width > viewportWidth - gap) {
      left = viewportWidth - tooltipRect.width - gap;
    }
    if (top < gap) top = gap;
    if (top + tooltipRect.height > viewportHeight - gap) {
      top = viewportHeight - tooltipRect.height - gap;
    }

    setPosition({ top, left });
    setPositioned(true);
  }, [placement]);

  // Use useLayoutEffect for initial positioning - runs synchronously
  // before browser paint, preventing flash at wrong position
  useLayoutEffect(() => {
    if (visible && triggerRef.current && tooltipRef.current) {
      calculatePosition();
    }
  }, [visible, calculatePosition]);

  // Use useEffect for scroll/resize listeners
  useEffect(() => {
    if (!visible) return;

    const handleScrollOrResize = () => {
      calculatePosition();
    };

    window.addEventListener("scroll", handleScrollOrResize, true);
    window.addEventListener("resize", handleScrollOrResize);

    return () => {
      window.removeEventListener("scroll", handleScrollOrResize, true);
      window.removeEventListener("resize", handleScrollOrResize);
    };
  }, [visible, calculatePosition]);

  // Escape key dismisses tooltip (WCAG 1.4.13 Content on Hover or Focus)
  useEffect(() => {
    if (!visible) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        hideImmediately();
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [visible, hideImmediately]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
    };
  }, []);

  return (
    <>
      <div
        ref={triggerRef}
        className={classNames("zendir-tooltip-trigger", className)}
        style={{
          display: "inline-flex",
          pointerEvents: "auto",
          maxWidth: "100%",
          width: "inherit",
        }}
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
        onFocus={showTooltip}
        onBlur={hideTooltip}
      >
        {children}
      </div>

      {visible &&
        ReactDOM.createPortal(
          <div
            ref={tooltipRef}
            role="tooltip"
            onMouseEnter={showTooltip}
            onMouseLeave={hideTooltip}
            style={{
              position: "fixed",
              top: positioned ? position.top : 0,
              left: positioned ? position.left : 0,
              width: "max-content",
              padding: `${tokens.spacing.sm} ${tokens.spacing.smd}`,
              backgroundColor: tokens.colors.background.elevated,
              color: tokens.colors.text.primary,
              fontSize: "0.75rem",
              fontWeight: 500,
              fontFamily: tokens.typography.fontFamily.primary,
              borderRadius: tokens.borderRadius.md,
              border: `1px solid ${tokens.colors.accent.primary}20`,
              boxShadow: positioned
                ? `${tokens.shadows.lg}, 0 0 20px ${tokens.colors.accent.primary}10`
                : "none",
              zIndex: 2147483100,
              maxWidth: "min(280px, calc(100vw - 24px))",
              whiteSpace: "normal",
              pointerEvents: "auto",
              visibility: positioned ? "visible" : "hidden",
              opacity: positioned ? 1 : 0,
              transition: positioned ? undefined : "none",
              animation: positioned
                ? `zendir-tooltip ${tokens.animation.duration.fast}ms ${tokens.animation.easing.default} both`
                : "none",
              backdropFilter: "blur(8px)",
            }}
          >
            {content}
            <style>
              {`@keyframes zendir-tooltip { from { opacity: 0; transform: scale(0.95) translateY(${placement === "bottom" ? "-4px" : placement === "top" ? "4px" : "0"}); } to { opacity: 1; transform: scale(1) translateY(0); } }
            @media (prefers-reduced-motion: reduce) { @keyframes zendir-tooltip { from, to { opacity: 1; transform: none; } } }`}
            </style>
          </div>,
          document.body,
        )}
    </>
  );
});

export default Tooltip;
